import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar
} from '@mui/material';
import { Edit, Delete, CheckCircle, Cancel, HelpOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import { getGroupPhotoUrl } from '../utils/mediaUrl';

const Grupos = () => {
  const { t } = useTranslation();
  const [groupName, setGroupName] = useState('');
  const [groupColor, setGroupColor] = useState('');
  const [cantera, setCantera] = useState(false);
  const [videosActionsVisible, setVideosActionsVisible] = useState(true);
  const [groups, setGroups] = useState([]);
  const [editGroup, setEditGroup] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Fichero seleccionado para upload
  const [selectedFile, setSelectedFile] = useState(null);

  // Vista previa de la imagen
  const [previewImage, setPreviewImage] = useState(null);

  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const section = process.env.REACT_APP_SECTION;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Ordenar grupos alfabéticamente u otro criterio
  const sortGroups = (groupsToSort) => {
    return [...groupsToSort].sort((a, b) =>
      a.libelle.localeCompare(b.libelle, 'es', { sensitivity: 'base' })
    );
  };

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await api.get('/api/groups');
      setGroups(sortGroups(response.data));
    } catch (error) {
      console.error('Error al obtener los grupos:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    // Verificar tamaño del archivo (500 Ko = 512000 bytes)
    if (file && file.size > 500 * 1024) {
      alert(t('grupos.fileTooLarge'));
      e.target.value = ''; // Limpiar el input
      setSelectedFile(null);
      setPreviewImage(null);
      return;
    }
    
    setSelectedFile(file);

    // Generar vista previa
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewImage(null);
    }
  };

  const handleAddGroup = async () => {
    if (!groupName || !groupColor) return;

    try {
      // Construcción del FormData
      const formData = new FormData();
      formData.append('libelle', groupName);
      formData.append('code_couleur', groupColor);
      formData.append('cantera', cantera);
      formData.append('videos_actions_visible', videosActionsVisible);
      formData.append('section', section);

      // Si es cantera y se ha seleccionado un fichero
      if (cantera && selectedFile) {
        formData.append('photo', selectedFile); // Campo 'photo' en el backend
      }

      const response = await api.post('/api/groups', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setGroups(sortGroups([...groups, response.data]));
      setGroupName('');
      setGroupColor('');
      setCantera(false);
      setVideosActionsVisible(true);
      setSelectedFile(null);
      setPreviewImage(null);
    } catch (error) {
      console.error('Error al añadir el grupo:', error);
    }
  };

  const handleEditGroup = (group) => {
    setEditGroup(group);
    setGroupName(group.libelle);
    setGroupColor(group.code_couleur);
    setCantera(!!group.cantera);
    setVideosActionsVisible(group.videos_actions_visible !== undefined ? !!group.videos_actions_visible : true);

    // Si el grupo es cantera, mostrar la imagen ya subida como vista previa
    if (group.cantera) {
      setPreviewImage(getGroupPhotoUrl(group.id));
    } else {
      setPreviewImage(null);
    }

    setSelectedFile(null);
  };

  const handleSaveEdit = async () => {
    if (!editGroup) return;

    try {
      // Construcción del FormData
      const formData = new FormData();
      formData.append('libelle', groupName);
      formData.append('code_couleur', groupColor);
      formData.append('cantera', cantera);
      formData.append('videos_actions_visible', videosActionsVisible);
      formData.append('section', section);

      // Si es cantera y se ha seleccionado un fichero nuevo
      if (cantera && selectedFile) {
        formData.append('photo', selectedFile);
      }

      await api.put(`/api/groups/${editGroup.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Actualizar la lista de grupos en local
      const updatedGroups = groups.map((g) =>
        g.id === editGroup.id
          ? {
              ...g,
              libelle: groupName,
              code_couleur: groupColor,
              cantera,
              videos_actions_visible: videosActionsVisible
            }
          : g
      );

      setGroups(sortGroups(updatedGroups));
      setGroupName('');
      setGroupColor('');
      setCantera(false);
      setVideosActionsVisible(true);
      setEditGroup(null);
      setSelectedFile(null);
      setPreviewImage(null);
    } catch (error) {
      console.error('Error al modificar el grupo:', error);
    }
  };

  const handleDeleteGroup = async (group) => {
    try {
      await api.delete(`/api/groups/${group.id}`);
      const updatedGroups = groups.filter((g) => g.id !== group.id);
      setGroups(sortGroups(updatedGroups));
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error al eliminar el grupo:', error);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        {t('grupos.title')}
      </Typography>

      <Grid container spacing={2} direction={isSmallScreen ? 'column' : 'row'} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <TextField
            label={t('grupos.groupName')}
            variant="outlined"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <Select
            value={groupColor}
            onChange={(e) => setGroupColor(e.target.value)}
            displayEmpty
            variant="outlined"
            fullWidth
          >
            <MenuItem value="" disabled>
              {t('grupos.selectColor')}
            </MenuItem>
            <MenuItem value="#ffcc80">{t('grupos.colors.orange')}</MenuItem>
            <MenuItem value="#90caf9">{t('grupos.colors.blue')}</MenuItem>
            <MenuItem value="#a5d6a7">{t('grupos.colors.green')}</MenuItem>
            <MenuItem value="#f48fb1">{t('grupos.colors.pink')}</MenuItem>
            <MenuItem value="#ffab91">{t('grupos.colors.lightOrange')}</MenuItem>
            <MenuItem value="#ce93d8">{t('grupos.colors.purple')}</MenuItem>
            <MenuItem value="#81c784">{t('grupos.colors.lightGreen')}</MenuItem>
            <MenuItem value="#64b5f6">{t('grupos.colors.skyBlue')}</MenuItem>
            <MenuItem value="#ba68c8">{t('grupos.colors.violet')}</MenuItem>
            <MenuItem value="#4fc3f7">{t('grupos.colors.lightBlue')}</MenuItem>
            <MenuItem value="#e57373">{t('grupos.colors.lightRed')}</MenuItem>
            <MenuItem value="#fff176">{t('grupos.colors.yellow')}</MenuItem>
          </Select>
        </Grid>

        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={cantera}
                onChange={(e) => setCantera(e.target.checked)}
                color="primary"
              />
            }
            label={t('grupos.cantera')}
          />
          {cantera && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={videosActionsVisible}
                    onChange={(e) => setVideosActionsVisible(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('grupos.videosActionsVisible')}
              />
              <Tooltip
                title={t('grupos.videosActionsTooltip')}
                arrow
                placement="top"
              >
                <HelpOutline sx={{ fontSize: 18, color: 'action.active', cursor: 'help' }} />
              </Tooltip>
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            onClick={editGroup ? handleSaveEdit : handleAddGroup}
            sx={{ backgroundColor: '#ff9800', color: 'white', height: '100%', width: '150px' }}
          >
            {editGroup ? t('grupos.saveChanges') : t('grupos.addGroup')}
          </Button>
        </Grid>

        {/* Input para subir foto (solo si es cantera) */}
        {cantera && (
          <Grid item xs={12}>
            <Box
              sx={{
                border: '1px dashed #ccc',
                padding: 2,
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <Typography variant="body1" sx={{ mb: 2 }}>
                {editGroup
                  ? t('grupos.modifyPhoto')
                  : t('grupos.uploadPhoto')}
              </Typography>
              <input type="file" accept="image/jpeg,image/jpg" onChange={handleFileChange} />
              {previewImage && (
                <Box mt={2}>
                  <img
                    src={previewImage}
                    alt={t('grupos.preview')}
                    style={{
                      display: 'block',
                      maxWidth: '800px',
                      width: '100%',
                      border: '1px solid #ccc',
                      margin: '0 auto'
                    }}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Listado de grupos existentes en tabla */}
      <Box>

        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>{t('grupos.table.name')}</strong></TableCell>
                <TableCell align="center"><strong>{t('grupos.table.color')}</strong></TableCell>
                <TableCell align="center"><strong>{t('grupos.table.cantera')}</strong></TableCell>
                <TableCell align="center"><strong>{t('grupos.table.photo')}</strong></TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <strong>{t('grupos.table.videosActionsVisible')}</strong>
                    <Tooltip
                      title={t('grupos.videosActionsTooltip')}
                      arrow
                      placement="top"
                    >
                      <HelpOutline sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center"><strong>{t('grupos.table.actions')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow
                  key={group.id}
                  sx={{
                    '&:hover': { backgroundColor: '#f9f9f9' },
                    '&:last-child td, &:last-child th': { border: 0 }
                  }}
                >
                  {/* Nombre del grupo */}
                  <TableCell>
                    <Chip
                      label={group.libelle}
                      sx={{
                        backgroundColor: group.code_couleur,
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>

                  {/* Color */}
                  <TableCell align="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: group.code_couleur,
                        borderRadius: '50%',
                        margin: '0 auto',
                        border: '2px solid #ccc'
                      }}
                    />
                  </TableCell>

                  {/* Cantera */}
                  <TableCell align="center">
                    {group.cantera ? (
                      <CheckCircle sx={{ color: 'green' }} />
                    ) : (
                      <Cancel sx={{ color: 'gray' }} />
                    )}
                  </TableCell>

                  {/* Foto (solo si es cantera) */}
                  <TableCell align="center">
                    {group.cantera ? (
                      <Tooltip
                        arrow
                        title={
                          <img
                            src={getGroupPhotoUrl(group.id)}
                            alt={t('grupos.enlargedPhoto')}
                            style={{ width: '300px', borderRadius: '4px' }}
                          />
                        }
                      >
                        <Avatar
                          src={getGroupPhotoUrl(group.id)}
                          alt={t('grupos.canteraPhoto')}
                          sx={{
                            width: 60,
                            height: 60,
                            margin: '0 auto',
                            cursor: 'pointer',
                            border: '2px solid #ccc'
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('grupos.notApplicable')}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Vídeos/Acciones Visibles */}
                  <TableCell align="center">
                    {group.cantera ? (
                      group.videos_actions_visible ? (
                        <CheckCircle sx={{ color: 'green' }} />
                      ) : (
                        <Cancel sx={{ color: 'red' }} />
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('grupos.notApplicable')}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Acciones */}
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleEditGroup(group)}
                      sx={{ color: '#ff9800' }}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={() => setConfirmDelete(group)}
                      sx={{ color: 'red' }}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Diálogo de confirmación para eliminar grupo */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogContent>
          <Typography variant="h6">
            {t('grupos.confirmDelete', { name: confirmDelete?.libelle })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ color: 'grey' }}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => handleDeleteGroup(confirmDelete)} sx={{ color: 'red' }}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Grupos;
